import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { RefreshCw, ExternalLink, CalendarPlus, BadgeCheck, Layers, XCircle, Bell, Download, Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { API } from "../../App";
import { labelledCollections } from "./aerofleetLabels";

// NOT a module-level constant: App.js imports AdminDashboard, which imports this file, so API is
// still uninitialised while this module loads (a top-level use crashed the whole app).
const base = () => `${API}/super-admin/fleet`;

const STATUS = {
  trialing: { label: "Em teste", cls: "bg-sky-500/15 text-sky-300 border-sky-500/30" },
  active: { label: "Subscrito", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  manual: { label: "Ativação manual", cls: "bg-violet-500/15 text-violet-300 border-violet-500/30" },
  past_due: { label: "Pagamento falhado", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  expired: { label: "Teste terminado", cls: "bg-red-500/15 text-red-300 border-red-500/30" },
  canceled: { label: "Cancelado", cls: "bg-red-500/15 text-red-300 border-red-500/30" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("pt-PT");
};

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : "Erro";
};

function detailLine(a) {
  const s = a.state;
  if (s.status === "manual") {
    return s.manual_until ? `Manual até ${fmtDate(s.manual_until)}` : "Manual, sem data de fim";
  }
  if (s.status === "trialing") return `Teste até ${fmtDate(s.trial_ends_at)} (${s.days_left} d)`;
  if (s.status === "expired") return `Teste terminou a ${fmtDate(s.trial_ends_at)}`;
  return "";
}

export default function AerofleetPanel() {
  const [accounts, setAccounts] = useState([]);
  const [counts, setCounts] = useState({});
  const [notify, setNotify] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // {kind, account}
  const [days, setDays] = useState("7");
  const [noEnd, setNoEnd] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  // Import of a fleet export into another school
  const [schools, setSchools] = useState([]);
  const [bundle, setBundle] = useState(null);
  const [bundleName, setBundleName] = useState("");
  const [targetId, setTargetId] = useState("");
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);

  // Delete an account (preview first, then type the organisation name)
  const [del, setDel] = useState(null); // {account, preview}
  const [confirmName, setConfirmName] = useState("");
  const [delUsers, setDelUsers] = useState(false);
  const [delFiles, setDelFiles] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const openDelete = async (account) => {
    setConfirmName("");
    setDelUsers(false);
    setDelFiles(false);
    try {
      const r = await axios.get(`${base()}/${account.school_id}/delete-preview`, { withCredentials: true });
      setDel({ account, preview: r.data });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      const r = await axios.post(
        `${base()}/${del.account.school_id}/delete-account`,
        { confirm_name: confirmName, delete_users: delUsers, delete_files: delFiles },
        { withCredentials: true },
      );
      toast.success(`Conta apagada. Cópia de segurança: ${r.data.backup_id}`);
      setDel(null);
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setDeleting(false);
    }
  };

  const downloadExport = async (account) => {
    try {
      const r = await axios.get(`${base()}/${account.school_id}/export`, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `aerofleet-${account.name || account.school_id}.json`.replace(/[^a-zA-Z0-9._-]+/g, "-");
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Não foi possível exportar a frota");
    }
  };

  const loadSchools = async () => {
    try {
      const r = await axios.get(`${API}/super-admin/schools`, { withCredentials: true });
      setSchools(r.data || []);
    } catch (e) {
      toast.error("Não foi possível carregar as escolas");
    }
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    setPreview(null);
    setBundle(null);
    setBundleName("");
    if (!file) return;
    try {
      const parsed = JSON.parse(await file.text());
      setBundle(parsed);
      setBundleName(file.name);
      if (!schools.length) await loadSchools();
    } catch (err) {
      toast.error("O ficheiro não é um JSON válido");
    }
  };

  const callImport = async (dryRun) => {
    setImporting(true);
    try {
      const r = await axios.post(
        `${base()}/import`,
        { target_school_id: targetId, bundle, dry_run: dryRun },
        { withCredentials: true },
      );
      if (dryRun) {
        setPreview(r.data);
      } else {
        toast.success("Frota importada");
        setPreview(null);
        setBundle(null);
        setBundleName("");
        await load();
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setImporting(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${base()}/accounts`, { withCredentials: true });
      setAccounts(r.data.accounts || []);
      setCounts(r.data.counts || {});
      setNotify(!!r.data.notify_signups);
    } catch (e) {
      toast.error(errMsg(e) || "Erro ao carregar contas Aerofleet");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const open = (kind, account) => {
    setDays(kind === "extend" ? "7" : "30");
    setNoEnd(false);
    setNote("");
    setDialog({ kind, account });
  };

  const run = async (request, okMessage) => {
    setBusy(true);
    try {
      await request();
      toast.success(okMessage);
      setDialog(null);
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const cfg = { withCredentials: true };
  const daysNum = parseInt(days, 10);
  const daysOk = Number.isInteger(daysNum) && daysNum >= 1 && daysNum <= (dialog?.kind === "extend" ? 365 : 3650);

  const submit = () => {
    const { kind, account } = dialog;
    const id = account.school_id;
    if (kind === "extend") return run(() => axios.post(`${base()}/${id}/extend-trial`, { days: daysNum }, cfg), "Teste prolongado");
    if (kind === "manual") {
      const body = noEnd ? { no_end: true, note } : { days: daysNum, note };
      return run(() => axios.post(`${base()}/${id}/manual-activation`, body, cfg), "Ativação manual guardada");
    }
    if (kind === "clear") return run(() => axios.delete(`${base()}/${id}/manual-activation`, cfg), "Ativação manual removida");
    if (kind === "product") {
      const next = account.product === "both" ? "fleet" : "both";
      return run(() => axios.put(`${base()}/${id}/product`, { product: next }, cfg), "Produto atualizado");
    }
    return null;
  };

  const toggleNotify = async (value) => {
    setNotify(value);
    try {
      await axios.put(`${base()}/settings/notify-signups`, { enabled: value }, cfg);
      toast.success(value ? "Vai receber um e-mail por cada nova inscrição" : "Avisos de inscrição desligados");
    } catch (e) {
      setNotify(!value);
      toast.error(errMsg(e));
    }
  };

  const summary = ["trialing", "manual", "active", "past_due", "expired", "canceled"]
    .filter((k) => counts[k])
    .map((k) => `${counts[k]} ${STATUS[k].label.toLowerCase()}`)
    .join(" · ");

  return (
    <div className="space-y-4" data-testid="aerofleet-panel">
      <Card className="instrument-panel">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center justify-between gap-2">
            <span>Aerofleet Center · Contas</span>
            <Button variant="outline" size="sm" onClick={load} disabled={loading} className="btn-secondary-cockpit text-xs">
              <RefreshCw className={`w-3 h-3 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Atualizar
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-xs">
          <p className="text-slate-400">
            {accounts.length} {accounts.length === 1 ? "conta" : "contas"}
            {summary ? ` — ${summary}` : ""}. A ativação manual prevalece sobre o Stripe enquanto durar; quando acaba, volta a valer o Stripe.
          </p>
          <div className="flex items-center gap-3">
            <Switch checked={notify} onCheckedChange={toggleNotify} data-testid="aerofleet-notify-switch" />
            <Label className="text-xs flex items-center gap-1.5">
              <Bell className="w-3 h-3" /> Enviar-me um e-mail por cada nova inscrição
            </Label>
          </div>
        </CardContent>
      </Card>

      {accounts.length === 0 && !loading && (
        <p className="text-sm text-slate-400" data-testid="aerofleet-empty">Ainda não há inscrições no Aerofleet Center.</p>
      )}

      {accounts.map((a) => {
        const st = STATUS[a.state.status] || { label: a.state.status, cls: "border-slate-600 text-slate-300" };
        const hasManual = a.state.status === "manual" || a.manual?.granted;
        return (
          <Card key={a.school_id} className="instrument-panel" data-testid={`aerofleet-account-${a.school_id}`}>
            <CardContent className="p-4 space-y-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-100 break-words">{a.name}</p>
                  <p className="text-xs text-slate-400 break-all">
                    {a.owner?.name ? `${a.owner.name} · ` : ""}{a.owner?.email || "sem dono associado"}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`px-2 py-0.5 rounded-full border text-xs ${st.cls}`}>{st.label}</span>
                  {a.product === "both" && (
                    <span className="px-2 py-0.5 rounded-full border text-xs border-orange-500/30 bg-orange-500/10 text-orange-300">Frota + Treino</span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <div><p className="text-slate-500">Inscrita</p><p className="text-slate-200">{fmtDate(a.created_at)}</p></div>
                <div><p className="text-slate-500">Aeronaves</p><p className="text-slate-200">{a.aircraft_count}</p></div>
                <div><p className="text-slate-500">Estado</p><p className="text-slate-200">{detailLine(a) || st.label}</p></div>
                <div>
                  <p className="text-slate-500">Stripe</p>
                  {a.stripe.customer_url ? (
                    <a href={a.stripe.customer_url} target="_blank" rel="noopener noreferrer" className="text-sky-300 inline-flex items-center gap-1 hover:underline">
                      Cliente <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : <span className="text-slate-400">Sem cliente</span>}
                  {a.stripe.subscription_url && (
                    <a href={a.stripe.subscription_url} target="_blank" rel="noopener noreferrer" className="text-sky-300 inline-flex items-center gap-1 hover:underline ml-3">
                      Subscrição <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>

              {a.manual?.granted && a.manual.note && (
                <p className="text-xs text-slate-400">Nota da ativação manual: {a.manual.note}</p>
              )}

              <div className="flex flex-wrap gap-2">
                {a.stored_status === "trialing" && (
                  <Button size="sm" variant="outline" className="btn-secondary-cockpit text-xs" onClick={() => open("extend", a)} data-testid="aerofleet-extend-btn">
                    <CalendarPlus className="w-3 h-3 mr-1.5" /> Prolongar teste
                  </Button>
                )}
                <Button size="sm" variant="outline" className="btn-secondary-cockpit text-xs" onClick={() => open("manual", a)} data-testid="aerofleet-manual-btn">
                  <BadgeCheck className="w-3 h-3 mr-1.5" /> {hasManual ? "Alterar ativação manual" : "Ativar manualmente"}
                </Button>
                {a.manual?.granted && (
                  <Button size="sm" variant="outline" className="btn-secondary-cockpit text-xs" onClick={() => open("clear", a)}>
                    <XCircle className="w-3 h-3 mr-1.5" /> Remover ativação manual
                  </Button>
                )}
                <Button size="sm" variant="outline" className="btn-secondary-cockpit text-xs" onClick={() => downloadExport(a)} data-testid="aerofleet-export-btn">
                  <Download className="w-3 h-3 mr-1.5" /> Exportar frota
                </Button>
                <Button size="sm" variant="outline" className="btn-secondary-cockpit text-xs" onClick={() => open("product", a)} data-testid="aerofleet-product-btn">
                  <Layers className="w-3 h-3 mr-1.5" /> {a.product === "both" ? "Só Frota" : "Frota + Treino"}
                </Button>
                <Button size="sm" variant="outline" className="text-xs border-red-500/40 text-red-300 hover:bg-red-500/10" onClick={() => openDelete(a)} data-testid="aerofleet-delete-btn">
                  <Trash2 className="w-3 h-3 mr-1.5" /> Apagar conta
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}

      <Dialog open={!!del} onOpenChange={(o) => { if (!o && !deleting) setDel(null); }}>
        <DialogContent className="bg-slate-950 border-slate-800" data-testid="aerofleet-delete-dialog">
          {del && (
            <>
              <DialogHeader>
                <DialogTitle>Apagar a conta "{del.preview.school.name}"</DialogTitle>
                <DialogDescription>
                  Apaga a organização e os dados dela. Fica uma cópia de segurança na base de dados. O Stripe não é alterado.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2 text-xs text-slate-300">
                <p className="text-slate-400">Dados que serão apagados:</p>
                {Object.keys(del.preview.collections).length === 0 ? (
                  <p>Nenhum registo de frota (a organização está vazia).</p>
                ) : (
                  <ul className="list-disc pl-5">
                    {labelledCollections(del.preview.collections).map((c) => <li key={c.name}>{c.label}: {c.count}</li>)}
                  </ul>
                )}
                {del.preview.pointer_users.length > 0 && (
                  <p>
                    Mantêm a conta e só perdem a ligação ao Aerofleet:{" "}
                    <strong>{del.preview.pointer_users.join(", ")}</strong> (podem voltar a inscrever-se do zero).
                  </p>
                )}
                {del.preview.fleet_only_users.length > 0 && (
                  <div className="flex items-start gap-3 rounded border border-slate-700 p-2">
                    <Switch checked={delUsers} onCheckedChange={setDelUsers} />
                    <Label className="text-xs">
                      Apagar também as contas que só existem por esta inscrição: <strong>{del.preview.fleet_only_users.join(", ")}</strong>
                    </Label>
                  </div>
                )}
                {del.preview.files > 0 && (
                  <div className="flex items-start gap-3 rounded border border-slate-700 p-2">
                    <Switch checked={delFiles} onCheckedChange={setDelFiles} />
                    <Label className="text-xs">Apagar também os {del.preview.files} ficheiros de documentos carregados (não ficam na cópia de segurança)</Label>
                  </div>
                )}
                <div>
                  <Label className="text-xs">Para confirmar, escreva o nome da organização: {del.preview.school.name}</Label>
                  <Input value={confirmName} onChange={(e) => setConfirmName(e.target.value)} data-testid="aerofleet-delete-confirm-input" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDel(null)} disabled={deleting} className="btn-secondary-cockpit">Cancelar</Button>
                <Button
                  onClick={doDelete}
                  disabled={deleting || confirmName.trim() !== (del.preview.school.name || "").trim()}
                  className="bg-red-600 hover:bg-red-500 text-white"
                  data-testid="aerofleet-delete-confirm-btn"
                >
                  {deleting ? "A apagar..." : "Apagar conta"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Card className="instrument-panel" data-testid="aerofleet-import-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Upload className="w-4 h-4" /> Importar frota para uma escola
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-xs">
          <p className="text-slate-400">
            Copia aeronaves, pilotos e instrutores de um ficheiro exportado para a escola escolhida. Não copia voos, documentos nem utilizadores,
            ignora os que já existem (mesma matrícula ou mesmo n.º de PU) e marca o que cria com um lote de importação. As escolas nunca são fundidas.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs">Ficheiro exportado (.json)</Label>
              <Input type="file" accept="application/json,.json" onChange={onPickFile} data-testid="aerofleet-import-file" />
            </div>
            <div>
              <Label className="text-xs">Escola de destino</Label>
              <select
                value={targetId}
                onChange={(e) => { setTargetId(e.target.value); setPreview(null); }}
                className="block h-9 rounded-md border border-slate-700 bg-slate-900 px-2 text-sm text-slate-100"
                data-testid="aerofleet-import-target"
              >
                <option value="">Escolher…</option>
                {schools.map((sc) => (
                  <option key={sc.school_id} value={sc.school_id}>{sc.name}</option>
                ))}
              </select>
            </div>
            <Button
              size="sm" variant="outline" className="btn-secondary-cockpit text-xs"
              disabled={!bundle || !targetId || importing} onClick={() => callImport(true)}
              data-testid="aerofleet-import-preview-btn"
            >
              Pré-visualizar
            </Button>
          </div>
          {bundleName && <p className="text-slate-500">Ficheiro: {bundleName}</p>}

          {preview && (
            <div className="rounded border border-slate-700 bg-slate-950/40 p-3 space-y-2" data-testid="aerofleet-import-preview">
              <p className="text-slate-200">Destino: <strong>{preview.target?.name}</strong></p>
              {["aircraft", "pilots", "instructors"].map((k) => (
                <p key={k} className="text-slate-300">
                  {{ aircraft: "Aeronaves", pilots: "Pilotos", instructors: "Instrutores" }[k]}:{" "}
                  <strong>{preview.new[k]}</strong> a criar
                  {preview.duplicates[k] > 0 && (
                    <span className="text-amber-300"> · {preview.duplicates[k]} já existem ({preview.duplicate_keys[k].join(", ")}) e ficam de fora</span>
                  )}
                </p>
              ))}
              {preview.problems?.length > 0 && (
                <p className="text-amber-300">Avisos: {preview.problems.join(" ")}</p>
              )}
              <Button
                size="sm" className="btn-cockpit text-xs"
                disabled={importing || (preview.new.aircraft + preview.new.pilots + preview.new.instructors === 0)}
                onClick={() => callImport(false)}
                data-testid="aerofleet-import-confirm-btn"
              >
                {importing ? "A importar..." : "Importar agora"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!dialog} onOpenChange={(o) => { if (!o && !busy) setDialog(null); }}>
        <DialogContent className="bg-slate-950 border-slate-800">
          {dialog && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {dialog.kind === "extend" && "Prolongar período de teste"}
                  {dialog.kind === "manual" && "Ativação manual"}
                  {dialog.kind === "clear" && "Remover ativação manual"}
                  {dialog.kind === "product" && (dialog.account.product === "both" ? "Voltar a só Frota" : "Dar também acesso a Treino")}
                </DialogTitle>
                <DialogDescription>{dialog.account.name}</DialogDescription>
              </DialogHeader>

              {dialog.kind === "extend" && (
                <div className="space-y-2">
                  <Label className="text-xs">Dias a acrescentar ao teste (1 a 365)</Label>
                  <Input type="number" min="1" max="365" value={days} onChange={(e) => setDays(e.target.value)} data-testid="aerofleet-days-input" />
                </div>
              )}

              {dialog.kind === "manual" && (
                <div className="space-y-3">
                  <p className="text-xs text-slate-400">
                    A conta fica com acesso total sem pagar, ignorando o estado do Stripe. Quando a ativação acabar, volta a valer o Stripe.
                  </p>
                  <div className="flex items-center gap-3">
                    <Switch checked={noEnd} onCheckedChange={setNoEnd} />
                    <Label className="text-xs">Sem data de fim</Label>
                  </div>
                  {!noEnd && (
                    <div>
                      <Label className="text-xs">Duração em dias (1 a 3650)</Label>
                      <Input type="number" min="1" max="3650" value={days} onChange={(e) => setDays(e.target.value)} data-testid="aerofleet-days-input" />
                    </div>
                  )}
                  <div>
                    <Label className="text-xs">Nota (só para si)</Label>
                    <Input value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} placeholder="Ex: oferta ao Aero Clube X" />
                  </div>
                </div>
              )}

              {dialog.kind === "clear" && (
                <p className="text-sm text-slate-300">A conta passa a seguir só o estado do Stripe (ou do período de teste).</p>
              )}

              {dialog.kind === "product" && (
                <p className="text-sm text-slate-300">
                  {dialog.account.product === "both"
                    ? "A organização deixa de ter acesso às funções de Treino. Os dados da frota não mudam."
                    : "A organização passa a ter acesso a Frota e a Treino. É a mesma escola: não é fundida com nenhuma outra."}
                </p>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => setDialog(null)} disabled={busy} className="btn-secondary-cockpit">Cancelar</Button>
                <Button
                  onClick={submit}
                  disabled={busy || ((dialog.kind === "extend" || (dialog.kind === "manual" && !noEnd)) && !daysOk)}
                  className="btn-cockpit"
                  data-testid="aerofleet-confirm-btn"
                >
                  {busy ? "A guardar..." : "Confirmar"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
